/**
 * Generic resource hooks — basit CRUD için kısa yol.
 *
 * Kullanım:
 *   const { data } = useResource('suppliers');           // GET list
 *   const { data } = useResource('suppliers', { id });   // GET detail
 *   const create = useCreate('suppliers');
 *   const update = useUpdate('suppliers');
 *   const remove = useRemove('suppliers');
 *   const act = useAction('purchase-orders', 'send');
 *
 * Tüm key'ler tenantId ile prefix'lenir (cache izolasyonu).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "./client";

function tenantId(): string {
  try {
    return JSON.parse(localStorage.getItem("auth_tenant") || "null")?.id || "anon";
  } catch {
    return "anon";
  }
}

export function useResource<T = any>(
  resource: string,
  opts?: {
    id?: string;
    filters?: Record<string, any>;
    // number → sabit aralık; fonksiyon → data'ya göre dinamik (smart polling,
    // örn. sadece aktif job varsa sık, hepsi terminal state'teyse durdur).
    // TanStack Query v5 callback formuyla uyumlu.
    refetchInterval?: number | ((query: any) => number | false | undefined);
    enabled?: boolean;
  },
) {
  const id = opts?.id;
  const filters = opts?.filters;
  return useQuery<T>({
    queryKey: [resource, tenantId(), id, filters],
    queryFn: async () => {
      const url = id ? `/api/v1/${resource}/${id}/` : `/api/v1/${resource}/`;
      return (await apiClient.get(url, { params: filters })).data;
    },
    refetchInterval: opts?.refetchInterval ?? false,
    enabled: opts?.enabled !== false,
  });
}

/**
 * Sayfalanmış liste — infinite scroll için.
 *
 * Backend DRF PageNumberPagination döndürür: { count, next, previous, results }.
 * `next` tam URL'dir; sadece ?page=N parametresini çıkarıp sıradaki sayfayı
 * isteriz. `pages` düzleştirilerek tek bir `items` dizisi elde edilir.
 *
 *   const { items, fetchNextPage, hasNextPage, isFetchingNextPage, total } =
 *     useInfiniteResource("modifiers", { search });
 */
export function useInfiniteResource<T = any>(
  resource: string,
  opts?: { search?: string; filters?: Record<string, any>; enabled?: boolean },
) {
  const search = opts?.search?.trim() || undefined;
  const filters = opts?.filters;

  const query = useInfiniteQuery({
    queryKey: [resource, "infinite", tenantId(), search, filters],
    initialPageParam: 1,
    enabled: opts?.enabled !== false,
    queryFn: async ({ pageParam }) => {
      const params: Record<string, any> = { ...filters, page: pageParam };
      if (search) params.search = search;
      const { data } = await apiClient.get(`/api/v1/${resource}/`, { params });
      return data as { count: number; next: string | null; results: T[] };
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage.next ? allPages.length + 1 : undefined,
  });

  const items: T[] = query.data?.pages.flatMap((p) => p.results) ?? [];
  const total: number = query.data?.pages[0]?.count ?? 0;

  return {
    items,
    total,
    isLoading: query.isLoading,
    isError: query.isError,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}

// ------------------------------------------------------------
// Önbellek yerinde düzeltme — güncelleme/silme sonrası sonsuz liste
// (ör. 13 sayfa ürün) baştan yeniden çekilmesin. Desteklenen şekiller:
// infinite {pages: [{results}]}, sayfalı {count, results}, düz dizi, detay.
// ------------------------------------------------------------
const replaceById = (list: any[], item: any) =>
  list.some((x) => x?.id === item.id) ? list.map((x) => (x?.id === item.id ? item : x)) : list;

export function patchItemInCache(old: any, item: any): any {
  if (!old || !item?.id) return old;
  if (Array.isArray(old.pages)) {
    return {
      ...old,
      pages: old.pages.map((pg: any) =>
        Array.isArray(pg?.results) && pg.results.some((x: any) => x?.id === item.id)
          ? { ...pg, results: replaceById(pg.results, item) }
          : pg,
      ),
    };
  }
  if (Array.isArray(old.results)) return { ...old, results: replaceById(old.results, item) };
  if (Array.isArray(old)) return replaceById(old, item);
  return old.id === item.id ? item : old;
}

export function removeItemFromCache(old: any, id: string): any {
  if (!old || !id) return old;
  if (Array.isArray(old.pages)) {
    const found = old.pages.some((pg: any) => pg?.results?.some((x: any) => x?.id === id));
    if (!found) return old;
    return {
      ...old,
      pages: old.pages.map((pg: any) => ({
        ...pg,
        results: (pg.results || []).filter((x: any) => x?.id !== id),
        count: typeof pg.count === "number" ? Math.max(0, pg.count - 1) : pg.count,
      })),
    };
  }
  if (Array.isArray(old.results)) {
    if (!old.results.some((x: any) => x?.id === id)) return old;
    return {
      ...old,
      results: old.results.filter((x: any) => x?.id !== id),
      count: typeof old.count === "number" ? Math.max(0, old.count - 1) : old.count,
    };
  }
  if (Array.isArray(old)) return old.filter((x) => x?.id !== id);
  return old;
}

// Sonsuz liste sorguları yerinde düzeltilir; geri kalanlar (detay, düz liste,
// filtreli görünümler) eskisi gibi geçersiz sayılır.
const notInfinite = (q: { queryKey: readonly unknown[] }) => q.queryKey[1] !== "infinite";

export function useCreate(resource: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) =>
      (await apiClient.post(`/api/v1/${resource}/`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: [resource] }),
  });
}

export function useUpdate(resource: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: FormData | ({ id: string } & Record<string, any>)) => {
      // FormData (multipart — görsel alanı varsa) durumunda destructuring
      // çalışmaz: id, FormData içinde bir form alanıdır, JS özelliği değil.
      // id'yi alandan okuyup URL'e koy, gövdeden çıkar (yoksa URL "undefined"
      // olur → 404).
      if (payload instanceof FormData) {
        const id = payload.get("id");
        if (!id) {
          throw new Error("useUpdate: FormData içinde 'id' alanı yok");
        }
        payload.delete("id");
        return (
          await apiClient.patch(`/api/v1/${resource}/${id}/`, payload)
        ).data;
      }
      const { id, ...data } = payload;
      return (await apiClient.patch(`/api/v1/${resource}/${id}/`, data)).data;
    },
    onSuccess: (updated) => {
      qc.setQueriesData({ queryKey: [resource] }, (old: any) => patchItemInCache(old, updated));
      return qc.invalidateQueries({ queryKey: [resource], predicate: notInfinite });
    },
  });
}

export function useRemove(resource: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.delete(`/api/v1/${resource}/${id}/`)).data,
    onSuccess: (_data, id) => {
      qc.setQueriesData({ queryKey: [resource] }, (old: any) => removeItemFromCache(old, id));
      return qc.invalidateQueries({ queryKey: [resource], predicate: notInfinite });
    },
  });
}

export function useAction(resource: string, actionName: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & Record<string, any>) =>
      (await apiClient.post(`/api/v1/${resource}/${id}/${actionName}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: [resource] }),
  });
}

// ============================================================
// Custom endpoints (non-CRUD)
// ============================================================
export function useBillingAccount() {
  return useQuery({
    queryKey: ["billing-account", tenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/billing/account/")).data,
    refetchInterval: 60_000,
  });
}

export function useBillingTransactions() {
  return useQuery({
    queryKey: ["billing-transactions", tenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/billing/transactions/")).data,
  });
}

export function useBillingPurchases() {
  return useQuery({
    queryKey: ["billing-purchases", tenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/billing/purchases/")).data,
  });
}

export function usePermissions() {
  return useQuery({
    queryKey: ["permissions", tenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/permissions/")).data,
  });
}

// Owner-only düzenlenebilir RBAC matrisi (tenant override).
export function useRbacMatrix() {
  return useQuery({
    queryKey: ["rbac-matrix", tenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/rbac/matrix/")).data,
  });
}

export function useUpdateRbacMatrix() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      matrix: Record<string, Record<string, string[]>>;
      app_access: Record<string, string[]>;
      // {role: [gizlenecek menü anahtarları]} — opsiyonel; gönderilmezse
      // backend menü görünürlüğünü değiştirmez.
      menu_hidden?: Record<string, string[]>;
    }) => (await apiClient.put("/api/v1/rbac/matrix/", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rbac-matrix"] });
      qc.invalidateQueries({ queryKey: ["permissions"] });
    },
  });
}
